import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Form, Modal, Row, Col, Badge, InputGroup, Spinner, Alert } from 'react-bootstrap';
import { FaEdit, FaTrash, FaSearch, FaPlus, FaBook, FaGraduationCap, FaPuzzlePiece, FaCheck, FaUsers, FaChalkboardTeacher } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { generateModulesUrl, generateEnrollmentsUrl } from '../../../utils/urlSecurity';
import api from '../../../api/apiClient';
import { toast } from 'react-toastify';

const CATEGORY_LABELS = {
  1: 'Business & Management',
  2: 'Technology & Programming',
  3: 'Personal Development',
  4: 'Workplace Skills'
};

const getCategoryLabel = (categoryInt) => {
  return CATEGORY_LABELS[categoryInt] || null;
};

const getDifficultyLevelId = (difficultyName) => {
  const difficultyMap = {
    'Beginner': 1,
    'Elementary': 2,
    'Intermediate': 3,
    'Advanced': 4,
    'Expert': 5
  };
  return difficultyMap[difficultyName] || 1;
};

const CourseManagement = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    duration: '',
    price: '',
    difficulty_level: 1,
    status: 1,
    course_image: null,
    skills: []
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [availableSkills, setAvailableSkills] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [skillSearchTerm, setSkillSearchTerm] = useState('');

  // --- Assign Instructor state ---
  const [showInstructorModal, setShowInstructorModal] = useState(false);
  const [instructorCourse, setInstructorCourse] = useState(null);
  const [allInstructors, setAllInstructors] = useState([]);
  const [assignedInstructorIds, setAssignedInstructorIds] = useState([]);
  const [instructorModalLoading, setInstructorModalLoading] = useState(false);
  const [instructorSaving, setInstructorSaving] = useState(false);
  const [instructorSearch, setInstructorSearch] = useState('');

  const handleOpenInstructorModal = async (course) => {
    setInstructorCourse(course);
    setInstructorSearch('');
    setShowInstructorModal(true);
    setInstructorModalLoading(true);
    try {
      const res = await api.courses.getCourseInstructors(course.id);
      if (res.data?.success) {
        setAllInstructors(res.data.data.instructors || []);
        setAssignedInstructorIds(res.data.data.assigned_ids || []);
      }
    } catch (err) {
      toast.error('Failed to load instructors: ' + (err.response?.data?.message || err.message));
    } finally {
      setInstructorModalLoading(false);
    }
  };

  const handleToggleInstructor = (id) => {
    setAssignedInstructorIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSaveInstructors = async () => {
    if (!instructorCourse) return;
    setInstructorSaving(true);
    try {
      await api.courses.setCourseInstructors(instructorCourse.id, assignedInstructorIds);
      toast.success('Instructors updated successfully');
      setShowInstructorModal(false);
    } catch (err) {
      toast.error('Failed to save instructors: ' + (err.response?.data?.message || err.message));
    } finally {
      setInstructorSaving(false);
    }
  };

  const filteredInstructorList = useMemo(() => {
    if (!instructorSearch.trim()) return allInstructors;
    const q = instructorSearch.toLowerCase();
    return allInstructors.filter(
      (u) =>
        `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q)
    );
  }, [allInstructors, instructorSearch]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await api.courses.getAll(true);
      if (response.data.success) {
        setCourses(response.data.data || []);
      } else {
        setCourses(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
    const fetchSkills = async () => {
      try {
        setSkillsLoading(true);
        const response = await api.skills.list();
        if (response.data?.success && Array.isArray(response.data.data)) {
          const sortedSkills = [...response.data.data].sort((a, b) => a.name.localeCompare(b.name));
          setAvailableSkills(sortedSkills);
        } else if (Array.isArray(response.data)) {
          setAvailableSkills(response.data);
        }
      } catch (error) {
        console.error('Error fetching skills:', error);
        toast.error('Failed to load skills: ' + (error.response?.data?.message || error.message));
      } finally {
        setSkillsLoading(false);
      }
    };

    fetchSkills();
  }, []);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (event) => {
    setFilterStatus(event.target.value);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Course name is required';
    if (!formData.description.trim()) errors.description = 'Course description is required';
    if (!formData.category) errors.category = 'Category is required';
    if (!formData.duration || isNaN(parseInt(formData.duration, 10)) || !Number.isInteger(Number(formData.duration)) || parseInt(formData.duration, 10) < 1) {
      errors.duration = 'Duration is required and must be a positive integer (mins)';
    }
    if (!formData.price || isNaN(parseFloat(formData.price))) errors.price = 'Price is required and must be a number';
    else if (parseFloat(formData.price) < 0) errors.price = 'Price cannot be negative';
    if (!formData.difficulty_level || formData.difficulty_level < 1 || formData.difficulty_level > 5) errors.difficulty_level = 'Difficulty level must be between 1 and 5';
    if (!editingCourse && !formData.course_image) errors.course_image = 'Course image is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleShowModal = async (course = null) => {
    if (course) {
      setEditingCourse(course);
      setFormData({
        name: course.title || course.name || '',
        description: course.description || '',
        category: course.category_id?.toString() || '',
        duration: course.duration?.toString() || '',
        price: course.price?.toString() || '',
        difficulty_level: getDifficultyLevelId(course.difficulty_level) || 1,
        status: course.status,
        course_image: course.course_image,
        skills: Array.isArray(course.skills)
          ? course.skills.map((skill) => skill.id?.toString() || `${skill}`)
          : Array.isArray(course.skill_ids)
            ? course.skill_ids.map((id) => id.toString())
            : []
      });
      setImagePreview(`${course.course_image}`);
    } else {
      setEditingCourse(null);
      setFormData({
        name: '',
        description: '',
        category: '',
        duration: '',
        price: '',
        difficulty_level: 1,
        status: 1,
        course_image: null,
        skills: []
      });
      setImagePreview(null);
    }
    setFormErrors({});
    setSkillSearchTerm('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCourse(null);
    setFormErrors({});
    setModalLoading(false);
  };

  const handleFormChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
      if (files[0]) {
        setImagePreview(URL.createObjectURL(files[0]));
      } else {
        setImagePreview(null);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'price' ? value.replace(/[^0-9.]/g, '') : value
      }));
      
      if (formErrors[name]) {
        setFormErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
    }
  };

  const filteredSkills = useMemo(() => {
    if (!skillSearchTerm.trim()) {
      return availableSkills;
    }
    const normalized = skillSearchTerm.trim().toLowerCase();
    return availableSkills.filter((skill) => skill.name?.toLowerCase().includes(normalized));
  }, [availableSkills, skillSearchTerm]);

  const selectedSkillRecords = useMemo(() => {
    if (!formData.skills?.length) {
      return [];
    }
    const skillIdSet = new Set(formData.skills.map((id) => id.toString()));
    return availableSkills.filter((skill) => skillIdSet.has(skill.id.toString()));
  }, [availableSkills, formData.skills]);

  const handleToggleSkill = (skillId) => {
    const idString = skillId.toString();
    setFormData((prev) => {
      const exists = prev.skills.includes(idString);
      const nextSkills = exists
        ? prev.skills.filter((value) => value !== idString)
        : [...prev.skills, idString];
      return {
        ...prev,
        skills: nextSkills
      };
    });
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setModalLoading(true);
      
      const payload = new FormData();
      payload.append('name', formData.name);
      payload.append('description', formData.description);
      payload.append('category_id', formData.category);
      payload.append('duration', formData.duration);
      payload.append('price', formData.price || '0');
      payload.append('difficulty_level', formData.difficulty_level);
      payload.append('status', formData.status);
      payload.append('skill_ids', JSON.stringify(formData.skills || []));
      
      if (formData.course_image) {
        payload.append('course_image', formData.course_image);
      }

      let courseId;
      if (editingCourse) {
        await api.courses.update(editingCourse.id, payload);
        courseId = editingCourse.id;
        toast.success(`Course "${formData.name}" has been updated.`);
      } else {
        const response = await api.courses.create(payload);
        courseId = response.data.success ? response.data.data?.id : response.data.id;
        toast.success(`Course "${formData.name}" has been created.`);
      }
      
      fetchCourses();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving course:', error);
      toast.error('Failed to save: ' + (error.response?.data?.message || error.message));
      setModalLoading(false);
    }
  };

  const handleDeleteClick = (course) => {
    setCourseToDelete(course);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!courseToDelete) return;
    
    try {
      setDeleteLoading(true);
      await api.courses.delete(courseToDelete.id);
      
      toast.success(`Course "${courseToDelete.title || courseToDelete.name}" has been deleted.`);
      
      fetchCourses();
      setShowDeleteModal(false);
      setCourseToDelete(null);
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error('Failed to delete: ' + (error.response?.data?.message || error.message));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setCourseToDelete(null);
  };

  const filteredCourses = courses.filter(course => {
    const searchFields = [
      course.title || course.name || '',
      course.description || '',
      course.category || ''
    ].join(' ').toLowerCase();
    
    const matchesSearch = !searchTerm || searchFields.includes(searchTerm.toLowerCase());
    
    let matchesFilter = true;
    switch(filterStatus) {
      case 'active':
        matchesFilter = parseInt(course.status) === 1 && parseInt(course.deleted) === 0;
        break;
      case 'inactive':
        matchesFilter = parseInt(course.status) === 0 && parseInt(course.deleted) === 0;
        break;
      case 'deleted':
        matchesFilter = parseInt(course.deleted) === 1;
        break;
      default:
        matchesFilter = parseInt(course.deleted) === 0;
        break;
    }
    
    return matchesSearch && matchesFilter;
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const getDifficultyBadge = (level) => {
    if (typeof level === 'string') {
      const colorMap = {
        'Beginner': 'success',
        'Elementary': 'info', 
        'Intermediate': 'warning',
        'Advanced': 'danger',
        'Expert': 'dark'
      };
      return { text: level, color: colorMap[level] || 'secondary' };
    }
    
    const levels = {
      1: { text: 'Beginner', color: 'success' },
      2: { text: 'Elementary', color: 'info' },
      3: { text: 'Intermediate', color: 'warning' },
      4: { text: 'Advanced', color: 'danger' },
      5: { text: 'Expert', color: 'dark' }
    };
    return levels[level] || { text: 'Unknown', color: 'secondary' };
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-h-96 p-4">
        <Spinner animation="border" role="status" className="text-purple-600 w-12 h-12">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          Manage Courses
        </h5>
      </div>
      
      {/* Search + Filter + Add */}
      <Row className="mb-3 g-2 sm:g-3">
        <Col xs={12} md={6} className="mb-2 mb-md-0">
          <InputGroup className="shadow-sm rounded-lg sm:rounded-xl overflow-hidden">
            <InputGroup.Text className="bg-white border-0 text-purple-600 px-3">
              <FaSearch className="w-4 h-4" />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search courses by name, description, or category..."
              value={searchTerm}
              onChange={handleSearch}
              className="border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
            />
          </InputGroup>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Form.Select 
            value={filterStatus} 
            onChange={handleFilterChange}
            className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
          >
            <option value="all">All Courses</option>
            <option value="active">Active Courses</option>
            <option value="inactive">Inactive Courses</option>
            <option value="deleted">Deleted Courses</option>
          </Form.Select>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Button 
              bsPrefix="custom-hamburger"
            onClick={() => handleShowModal()}
      className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 "
          >
            <FaPlus className="w-4 h-4" />
            <span className="whitespace-nowrap">Add Course</span>
          </Button>
        </Col>
      </Row>

      {/* No Data */}
      {filteredCourses.length === 0 ? (
        <Alert variant="info" className="text-center py-6 sm:py-8 rounded-xl sm:rounded-2xl bg-white/80 backdrop-blur shadow-md border border-purple-100">
          <p className="text-sm sm:text-base font-medium text-purple-700">
            {searchTerm || filterStatus !== 'all' 
              ? 'No courses found matching your criteria.' 
              : 'No courses found. Click "Add Course" to create the first course.'
            }
          </p>
        </Alert>
      ) : (
        <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl sm:rounded-2xl shadow-lg bg-white">
          <Table bordered hover className="m-0 min-w-max text-xs sm:text-sm">
            <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <tr>
                <th className="py-2 px-3 font-bold text-left">Course Name</th>
                <th className="py-2 px-3 font-bold text-left">Category</th>
                <th className="py-2 px-3 font-bold text-left">Duration (mins)</th>
                <th className="py-2 px-3 font-bold text-left">Difficulty</th>
                <th className="py-2 px-3 font-bold text-left">Status</th>
                <th className="py-2 px-3 font-bold text-left">Created</th>
                <th className="py-2 px-3 font-bold text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCourses.map((course) => (
                <tr key={course.id} className="border-b border-purple-100 hover:bg-purple-50/50 transition-all">
                  <td className="py-2 px-3">
                    <div className="d-flex align-items-center gap-2">
                 
                      <div>
                        <div className="fw-semibold text-gray-800 text-xs sm:text-sm">
                          {course.title || course.name}
                          {parseInt(course.deleted) === 1 && (
                            <Badge bg="danger" className="ms-2 text-xs">Deleted</Badge>
                          )}
                        </div>
                        {course.description && (
                          <small className="text-muted text-xs">
                            {course.description.length > 50 ? course.description.substring(0, 50) + '...' : course.description}
                          </small>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-3 text-gray-600 text-xs sm:text-sm">{course.category || '-'}</td>
                  <td className="py-2 px-3 text-left text-gray-600 text-xs sm:text-sm">{course.duration != null ? `${course.duration} mins` : '-'}</td>
                  <td className="py-2 px-3 text-left">
                    <Badge bg={getDifficultyBadge(course.difficulty_level).color} className="px-2 py-1 text-xs">
                      {getDifficultyBadge(course.difficulty_level).text}
                    </Badge>
                  </td>
                  <td className="py-2 px-3 text-left">
                    <Badge bg={parseInt(course.status) === 1 ? 'success' : 'danger'} className="px-2 py-1 text-xs">
                      {parseInt(course.status) === 1 ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="py-2 px-3 text-left text-gray-600 text-xs sm:text-sm">{formatDate(course.createdAt)}</td>
                  <td className="py-2 px-3">
                    <div className="d-flex gap-1 justify-content-left">
                      <Button
                       bsPrefix="custom-hamburger"
                        onClick={() => handleShowModal(course)}
                        title="Edit Course"
                        className="border-purple-300 text-purple-600 hover:bg-purple-100 rounded p-1 "
                      >
                        <FaEdit className="w-5 h-5" />
                      </Button>
                      <Button
                       bsPrefix="custom-hamburger"
                        onClick={() => navigate(generateModulesUrl(course.id))}
                        title="Manage Modules"
                        className="border-gray-300 text-gray-600 hover:bg-gray-100 rounded p-1 "
                      >
                        <FaPuzzlePiece className="w-5 h-5" />
                      </Button>
                      <Button
                        bsPrefix="custom-hamburger"
                        onClick={() => navigate(generateEnrollmentsUrl(course.id))}
                        title="Learner Enrollment"
                        className="border-blue-300 text-blue-600 hover:bg-blue-50 rounded p-1 "
                      >
                        <FaUsers className="w-5 h-5" />
                      </Button>
                      <Button
                        bsPrefix="custom-hamburger"
                        onClick={() => handleOpenInstructorModal(course)}
                        title="Assign Instructor"
                        className="border-indigo-300 text-indigo-600 hover:bg-indigo-50 rounded p-1"
                      >
                        <FaChalkboardTeacher className="w-5 h-5" />
                      </Button>
                      {parseInt(course.deleted) === 0 ? (
                        <Button
                        bsPrefix="custom-hamburger"
                          onClick={() => handleDeleteClick(course)}
                          title="Delete Course"
                          className="border-red-300 text-red-600 hover:bg-red-50 rounded p-1 "
                        >
                          <FaTrash className="w-5 h-5" />
                        </Button>
                      ) : (
                        <Button
                         bsPrefix="custom-hamburger"
                          onClick={async (e) => {
                            e.preventDefault();
                            try {
                              await api.courses.restore(course.id);
                              toast.success(`Course "${course.title || course.name}" has been restored`);
                              fetchCourses();
                            } catch (error) {
                              toast.error('Failed to restore course: ' + (error.response?.data?.message || error.message));
                            }
                          }}
                          title="Restore Course"
                          className="border-green-300 text-green-600 hover:bg-green-50 rounded p-1 "
                        >
                          <FaCheck className="w-5 h-5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Add/Edit Course Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered fullscreen="sm-down">
        <Modal.Header closeButton  closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4">
          <Modal.Title className="font-bold text-base sm:text-lg">
            {editingCourse ? 'Edit Course' : 'Add New Course'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0 sm:p-5 bg-gray-50">
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Course Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                isInvalid={!!formErrors.name}
                placeholder="Enter course name"
                className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.name}
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Description <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                isInvalid={!!formErrors.description}
                placeholder="Enter course description (optional)"
                className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.description}
              </Form.Control.Feedback>
            </Form.Group>
            
            <Row className="mb-3 g-2 sm:g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Category <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="category"
                    value={formData.category}
                    onChange={handleFormChange}
                    isInvalid={!!formErrors.category}
                    className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                  >
                    <option value="">Select Category (optional)</option>
                    <option value="1">Business & Management</option>
                    <option value="2">Technology & Programming</option>
                    <option value="3">Personal Development</option>
                    <option value="4">Workplace Skills</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {formErrors.category}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Duration (mins) <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    name="duration"
                    type="number"
                    min="1"
                    step="1"
                    value={formData.duration}
                    onChange={handleFormChange}
                    isInvalid={!!formErrors.duration}
                    placeholder="Enter duration in mins"
                    className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.duration}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3 g-2 sm:g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Course Price (USD) <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    name="price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={handleFormChange}
                    isInvalid={!!formErrors.price}
                    placeholder="Enter course price"
                    className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.price}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            
            <Row className="mb-3 g-2 sm:g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Difficulty Level <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="difficulty_level"
                    value={formData.difficulty_level}
                    onChange={handleFormChange}
                    isInvalid={!!formErrors.difficulty_level}
                    className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                  >
                    <option value={1}>1 - Beginner</option>
                    <option value={2}>2 - Elementary</option>
                    <option value={3}>3 - Intermediate</option>
                    <option value={4}>4 - Advanced</option>
                    <option value={5}>5 - Expert</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {formErrors.difficulty_level}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Status <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status?.toString()}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        status: parseInt(e.target.value)
                      }));
                    }}
                    isInvalid={!!formErrors.status}
                    className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                  >
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {formErrors.status}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Linked Skills</Form.Label>
              <InputGroup className="mb-2">
                <InputGroup.Text className="bg-white border-0 text-purple-600">
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="search"
                  placeholder="Search skills"
                  value={skillSearchTerm}
                  onChange={(event) => setSkillSearchTerm(event.target.value)}
                  disabled={skillsLoading || availableSkills.length === 0}
                  autoComplete="off"
                  className="border-0 focus:ring-2 focus:ring-purple-500 text-sm"
                />
              </InputGroup>
              <div className="border rounded-lg p-2 max-h-56 overflow-y-auto bg-white/80">
                {skillsLoading ? (
                  <div className="text-muted text-sm">Loading skills...</div>
                ) : filteredSkills.length === 0 ? (
                  <div className="text-muted text-sm">No skills found.</div>
                ) : (
                  filteredSkills.map((skill) => {
                    const idString = skill.id.toString();
                    return (
                      <Form.Check
                        key={skill.id}
                        id={`skill-${skill.id}`}
                        type="checkbox"
                        label={skill.name}
                        checked={formData.skills.includes(idString)}
                        onChange={() => handleToggleSkill(skill.id)}
                        className="mb-1 text-sm"
                      />
                    );
                  })
                )}
              </div>
              {selectedSkillRecords.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {selectedSkillRecords.map((skill) => (
                    <Badge key={skill.id} bg="info" className="text-white text-xs px-2 py-1">
                      {skill.name}
                    </Badge>
                  ))}
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">
                Course Image 
                {!editingCourse && <span className="text-danger">*</span>}
                {editingCourse && <span className="text-muted">(optional - leave empty to keep current image)</span>}
              </Form.Label>
              <Form.Control
                name="course_image"
                type="file"
                accept="image/*"
                onChange={handleFormChange}
                isInvalid={!!formErrors.course_image}
                required={!editingCourse}
                className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.course_image}
              </Form.Control.Feedback>
              {imagePreview && (
                <div className="mt-2">
                  <div className="w-full max-w-xs h-40 bg-gray-100 rounded-lg border-2 border-purple-200 overflow-hidden shadow-md">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 p-3 sm:p-4">
          <Row className="w-100 g-2">
            <Col xs={6}>
              <Button 
                bsPrefix="custom-hamburger"
                onClick={handleCloseModal} 
                disabled={modalLoading}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 "
              >
                Cancel
              </Button>
            </Col>
            <Col xs={6}>
              <Button 
                bsPrefix="custom-hamburger"
                onClick={handleSave} 
                disabled={modalLoading}
      className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 "
              >
                {modalLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {editingCourse ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    {editingCourse ? 'Update' : 'Create'} Course
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered fullscreen="sm-down">
        <Modal.Header closeVariant="white" closeButton className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="text-sm sm:text-base">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-10 bg-gray-50">
          <div className="mb-3 flex justify-center items-center">
            <FaGraduationCap size={50} className="text-gray-400" />
          </div>
          <h5 className="font-bold text-gray-800 text-sm sm:text-base">Delete Course</h5>
          <p className="text-muted text-xs sm:text-sm">
            Are you sure you want to delete{' '}
            <strong>{courseToDelete ? (courseToDelete.title || courseToDelete.name) : ''}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 p-3">
          <Row className="w-100 g-2">
            <Col xs={6}>
              <Button 
                bsPrefix="custom-hamburger" 
                onClick={handleDeleteCancel}
                disabled={deleteLoading}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col xs={6}>
              <Button 
                bsPrefix="custom-hamburger" 
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                 className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {deleteLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete Course'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* ── Assign Instructor Modal ── */}
      <Modal
        show={showInstructorModal}
        onHide={() => setShowInstructorModal(false)}
        centered
        size="lg"
        fullscreen="sm-down"
      >
        <Modal.Header
          closeButton
          closeVariant="white"
          className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-0 py-3 px-4"
        >
          <Modal.Title className="text-sm sm:text-base flex items-center gap-2">
            <FaChalkboardTeacher />
            Assign Instructors — {instructorCourse?.title || instructorCourse?.name}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-0 bg-gray-50">
          {instructorModalLoading ? (
            <div className="flex justify-center items-center py-16">
              <Spinner animation="border" variant="indigo" />
            </div>
          ) : (
            <>
              {/* Search bar */}
              <div className="p-3 border-b border-indigo-100 bg-white">
                <InputGroup className="shadow-sm rounded-lg overflow-hidden">
                  <InputGroup.Text className="bg-white border-0 text-indigo-500 px-3">
                    <FaSearch className="w-4 h-4" />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search instructors by name or email…"
                    value={instructorSearch}
                    onChange={(e) => setInstructorSearch(e.target.value)}
                    className="border-0 focus:ring-2 focus:ring-indigo-400 text-sm"
                  />
                </InputGroup>
              </div>

              {/* Summary badges */}
              <div className="flex gap-2 px-4 pt-3 pb-1">
                <Badge bg="success" className="text-xs px-2 py-1">
                  {assignedInstructorIds.length} assigned
                </Badge>
                <Badge bg="secondary" className="text-xs px-2 py-1">
                  {allInstructors.length - assignedInstructorIds.length} unassigned
                </Badge>
              </div>

              {filteredInstructorList.length === 0 ? (
                <div className="text-center py-10 text-gray-500 text-sm">
                  {allInstructors.length === 0
                    ? 'No platform-level instructors found.'
                    : 'No instructors match your search.'}
                </div>
              ) : (
                <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-96 overflow-y-auto">
                  {filteredInstructorList.map((inst) => {
                    const isAssigned = assignedInstructorIds.includes(inst.id);
                    return (
                      <div
                        key={inst.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleToggleInstructor(inst.id)}
                        onKeyDown={(e) => e.key === 'Enter' && handleToggleInstructor(inst.id)}
                        aria-label={`Toggle instructor ${inst.first_name} ${inst.last_name}`}
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all duration-150 select-none
                          ${isAssigned
                            ? 'border-indigo-500 bg-indigo-50 shadow-sm'
                            : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/40'
                          }`}
                      >
                        {/* Avatar */}
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0
                          ${isAssigned ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : 'bg-gradient-to-br from-gray-400 to-gray-500'}`}>
                          {inst.first_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm font-semibold text-gray-800 truncate">{inst.first_name} {inst.last_name}</p>
                          <p className="text-xs text-gray-500 truncate">{inst.email}</p>
                        </div>

                        {/* Checkbox indicator */}
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0
                          ${isAssigned ? 'bg-indigo-500 border-indigo-500' : 'border-gray-300 bg-white'}`}>
                          {isAssigned && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="bg-gray-100 border-0 p-3">
          <Row className="w-100 g-2">
            <Col xs={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={() => setShowInstructorModal(false)}
                disabled={instructorSaving}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg shadow-md transition-all text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col xs={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleSaveInstructors}
                disabled={instructorSaving || instructorModalLoading}
                className="w-full border-1 border-indigo-600 bg-white text-indigo-600 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {instructorSaving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Saving…
                  </>
                ) : (
                  'Save Assignments'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CourseManagement;